import { useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { NavLink } from 'react-router-dom';
import { color } from '../../../app/theme';
import {
  validateMessages,
  validateText,
  validateMax,
} from '../../../app/utils';

const useStyles = makeStyles(
  () => ({
    root: {
      position: 'fixed',
      top: 80,
      left: 0,
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      width: 240,
      height: 'calc(100vh - 80px)',
      backgroundColor: color.$3,
      color: color.$10,
    },
    menu: {
      flex: '1 1 100%',
      width: '100%',
      overflowY: 'auto',
    },
    version: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: 44,
      textAlign: 'center',
      fontSize: 11,
      lineHeight: '11px',
      color: '#989898',
      padding: '16px 0',
    },
    upgrade: {
      width: '100%',
      textAlign: 'center',
    },
    titleBlock: {
      padding: '24px 24px 12px',
      display: 'flex',
      justifyContent: 'left',
      alignItems: 'center',
      borderBottom: '1px solid rgba(239, 239, 239, 0.15)',
      '& > img': {
        marginRight: 6,
      },
    },
    basicSettingTitle: {
      position: 'relative',
      color: color.$10,
      lineHeight: 1,
      fontWeight: 500,
      fontSize: 15,
    },
    navLink: {
      cursor: 'pointer',
      position: 'relative',
      display: 'block',
      height: 48,
      lineHeight: '48px',
      fontSize: 17,
      color: color.$7,
      textDecoration: 'none',
      padding: '0 24px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&.active': {
        backgroundColor: 'rgba(224, 224, 224, 0.1)',
      },
      '&:hover': {
        backgroundColor: 'rgba(224, 224, 224, 0.1)',
        '& > div': {
          display: 'block',
        },
      },
    },
    'root-error': {
      borderLeft: `4px solid ${color.$17}`,
      paddingLeft: '20px',
    },
  }),
  { name: 'SidebarContainer' },
);

function SidebarContainer({ provider }) {
  const classes = useStyles();
  const { orgId } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { data, isSaved } = useSelector(store => store.ecBinding);

  const isOrderPage = pathname === `/${provider}/${orgId}/order`;
  const isTokenPage = pathname === `/${provider}/${orgId}/connect`;

  const handleCancelStepLink = () => {
    // e.preventDefault();
  };

  if (isTokenPage) return null;

  return (
    <div className={classes.root}>
      <div className={classes.menu}>
        <div className={classes.titleBlock}>
          <Typography variant="subtitle1" className={classes.basicSettingTitle}>
            {t('ecBinding:sidebar.subtitle1')}
          </Typography>
        </div>
        {isOrderPage && (
          <NavLink
            className={classnames(classes.navLink, {
              [classes['root-error']]:
                isSaved &&
                !(
                  validateText(data?.actions?.orderSearch?.keyword) &&
                  validateText(data?.actions?.orderSearch?.messageNoOrder)
                ),
            })}
            onClick={handleCancelStepLink}
            to={`/${provider}/${orgId}/order`}
            activeStyle={{
              backgroundColor: 'rgba(224, 224, 224, 0.1)',
            }}
          >
            {t('ecBinding:sidebar.order')}
          </NavLink>
        )}
        {!isOrderPage && (
          <>
            <NavLink
              className={classnames(classes.navLink, {
                [classes['root-error']]:
                  isSaved &&
                  !(
                    validateText(data?.liffSetting?.liffId) &&
                    validateText(data?.liffSetting?.channelId) &&
                    validateText(data?.liffSetting?.channelSecret)
                  ),
              })}
              onClick={handleCancelStepLink}
              to={`/${provider}/${orgId}/step1`}
              activeStyle={{
                backgroundColor: 'rgba(224, 224, 224, 0.1)',
              }}
            >
              {t('ecBinding:sidebar.step1')}
            </NavLink>
            <NavLink
              className={classnames(classes.navLink, {
                [classes['root-error']]:
                  isSaved &&
                  !(
                    validateText(data?.policy?.pageTitle) &&
                    validateMax(data?.policy?.pageTitle, 20) &&
                    validateText(data?.policy?.title) &&
                    validateMax(data?.policy?.title, 12) &&
                    validateText(data?.policy?.content)
                  ),
              })}
              onClick={handleCancelStepLink}
              to={`/${provider}/${orgId}/step2`}
              activeStyle={{
                backgroundColor: 'rgba(224, 224, 224, 0.1)',
              }}
            >
              {t('ecBinding:sidebar.step2')}
            </NavLink>
            <NavLink
              className={classnames(classes.navLink, {
                [classes['root-error']]:
                  isSaved &&
                  !(
                    validateText(data?.bindPage?.pageTitle) &&
                    validateMax(data?.bindPage?.pageTitle, 20)
                  ),
              })}
              onClick={handleCancelStepLink}
              to={`/${provider}/${orgId}/step3`}
              activeStyle={{
                backgroundColor: 'rgba(224, 224, 224, 0.1)',
              }}
            >
              {t('ecBinding:sidebar.step3')}
            </NavLink>
            <div className={classes.titleBlock} style={{ marginTop: 8 }}>
              <Typography
                variant="subtitle2"
                className={classes.basicSettingTitle}
              >
                {t('ecBinding:sidebar.subtitle2')}
              </Typography>
            </div>
            <NavLink
              className={classnames(classes.navLink, {
                [classes['root-error']]:
                  isSaved &&
                  !validateMessages(data?.bindResult?.success?.messages),
              })}
              onClick={handleCancelStepLink}
              to={`/${provider}/${orgId}/step4`}
              activeStyle={{
                backgroundColor: 'rgba(224, 224, 224, 0.1)',
              }}
            >
              {t('ecBinding:sidebar.step4')}
            </NavLink>
            <NavLink
              className={classnames(classes.navLink, {
                [classes['root-error']]:
                  isSaved &&
                  !validateMessages(data?.bindResult?.fail?.messages),
              })}
              onClick={handleCancelStepLink}
              to={`/${provider}/${orgId}/step5`}
              activeStyle={{
                backgroundColor: 'rgba(224, 224, 224, 0.1)',
              }}
            >
              {t('ecBinding:sidebar.step5')}
            </NavLink>
          </>
        )}
      </div>
      <div className={classes.version}>
        {t('sidebar.version', { icon: '\u26A1' })}
      </div>
    </div>
  );
}

export default SidebarContainer;
